<template>
    <USlideover :model-value="open" @update:model-value="$emit('update:open', $event)" :ui="{ width: 'max-w-2xl' }">
        <UForm ref="form" @submit="onSubmit" :schema="schema" :state="model" :validate-on="['submit']" class="h-full">
            <UCard class="h-full flex flex-col overflow-y-auto" :ui="{ ring: '', divide: '' }">
                <template #header>
                    <div class="flex items-center justify-between">
                        <h3 class="text-base font-medium leading-6 text-gray-800">
                            New Segment
                        </h3>
                    </div>
                </template>
                <OverlaySegmentForm v-model="model" />
                <template #footer>
                    <div class="flex items-center justify-end space-x-2">
                        <UButton @click="dismiss()" size="lg" color="white">
                            Cancel
                        </UButton>
                        <UButton type="submit" size="lg" :loading="loading" color="black">
                            Save
                        </UButton>
                    </div>
                </template>
            </UCard>
        </UForm>
    </USlideover>
</template>

<script setup lang="ts">
import { z } from 'zod'
import type { FormSubmitEvent } from '#ui/types'
import type { Filter } from '~/types/filter'
import type { CustomerFilter } from '~/types/customer-filter'
import { chipsToCustomerFilter, customerFilterToChips, customerFilterTemplate } from '~/utils/customer-filter-chips'

interface PickedCustomer { id: string; number: number; name: string }
interface Model { name: string; description: string; mode: 'filtered' | 'manual'; chips: Filter[]; includes: PickedCustomer[]; excludes: PickedCustomer[] }

const emit = defineEmits<{
    'update:open': [boolean]
    'reload': [boolean]
}>()

const props = defineProps<{
    open: boolean
    seed?: { filters: CustomerFilter; source_view_id?: string }
}>()

const form = ref()
const loading = ref(false)
const model = ref<Model>({ name: '', description: '', mode: 'filtered', chips: customerFilterTemplate(), includes: [], excludes: [] })
let sourceViewId: string | null = null

const schema = z.object({
    name: z.string().min(1, 'Name is required'),
    description: z.string().optional(),
})

watch(() => props.open, (open) => {
    if (open) {
        model.value = { name: '', description: '', mode: 'filtered', chips: customerFilterTemplate(), includes: [], excludes: [] }
        sourceViewId = null
        if (props.seed) {
            model.value.chips = customerFilterToChips(props.seed.filters, customerFilterTemplate())
            sourceViewId = props.seed.source_view_id ?? null
        }
    }
})

async function onSubmit(event: FormSubmitEvent<z.infer<typeof schema>>) {
    loading.value = true

    try {
        await $fetch('/api/segments', {
            method: 'POST',
            body: {
                name: event.data.name,
                description: event.data.description,
                filters: model.value.mode === 'filtered' ? chipsToCustomerFilter(model.value.chips) : {},
                source_view_id: model.value.mode === 'filtered' ? sourceViewId : null,
                members: model.value.mode === 'manual'
                    ? model.value.includes.map(c => ({ customer_id: c.id, mode: 'include' }))
                    : [],
            }
        })
        dismiss(true)
    } catch (error) {
        console.error('Create error:', error)
    }

    loading.value = false
}

function dismiss(reload: boolean = false) {
    emit('update:open', false)
    if (reload) {
        emit('reload', true)
    }
}
</script>
